import { useMutation } from '@tanstack/react-query';
import { addDays, parseISO } from 'date-fns';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { LicenseModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { formattedDate } from '../../../utils/dateUtils';
import { isDefined } from '../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useOptionalUserState } from '../../state/user-state/user-state';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { InfoBox } from '../info-box/info-box';
import { useLicense } from './use-license';

type Props = {
  expiredLicense?: LicenseModel;
  onBuyButtonClicked: () => void;
  onClose: () => void;
};

/**
 * Currently only one license can be expired when user click a project in the project list view.
 * TODO: When introducing a `validUntil` to other licenses, this dialog must display all licenses
 * TODO: that has expired. And then the user can take action on each one.
 */
const DesignLicenseExpiredDialog = (props: Props) => {
  const { t } = useTranslation();
  const userState = useOptionalUserState();
  const isActiveRoleCompanyAdmin = userState?.isActiveRoleCompanyAdmin() ?? false;
  const { latestLicenseDemoPeriod } = useLicense(props.expiredLicense);

  const sendContactMeRequest = useMutation({
    mutationFn: async () =>
      SkyMapAxiosServiceFactory.instance.createUserServiceV0().sendContactUserEmailToSkyMapSales({
        body: {
          companyId: props.expiredLicense!.companyId,
        },
      }),
    onError() {
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: t('designLicenseExpiredDialog.contactMe.requestFailed', {
          ns: 'components',
        }),
      });
    },
    onSuccess() {
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('designLicenseExpiredDialog.contactMe.requestSuccessful', {
          ns: 'components',
        }),
      });

      props.onClose();
    },
  });

  // Handle any case where a license is not found.
  if (!isDefined(latestLicenseDemoPeriod)) {
    return (
      <Dialog width={600} onClose={props.onClose}>
        {{
          header: t('designLicenseExpiredDialog.title.noLicenseFound', { ns: 'components' }),
          content: (
            <Content>
              <InfoBox color="yellow">
                <Trans
                  components={{
                    br: <br />,
                  }}
                  i18nKey="designLicenseExpiredDialog.noLicenseFound"
                  ns="components"
                />
              </InfoBox>
            </Content>
          ),
          footer: {
            right: (
              <>
                <Button variant="text" onClick={props.onClose}>
                  {t('close', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>
    );
  }

  return (
    <Dialog width={600} onClose={props.onClose}>
      {{
        header: t('designLicenseExpiredDialog.title.licenseFound', { ns: 'components' }),
        content: (
          <Content>
            <span>
              <Trans
                components={{
                  bold: <strong />,
                }}
                i18nKey="designLicenseExpiredDialog.trialPeriodExpired"
                ns="components"
                values={{
                  companyName: props.expiredLicense!.company!.name,
                  expiredDate: formattedDate(
                    addDays(parseISO(latestLicenseDemoPeriod.validUntil), -1),
                  ),
                }}
              />
            </span>

            {isActiveRoleCompanyAdmin ? (
              <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }} topMargin={true}>
                {t('designLicenseExpiredDialog.infoCompanyAdmin', { ns: 'components' })}
              </InfoBox>
            ) : (
              <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }} topMargin={true}>
                {t('designLicenseExpiredDialog.infoProjectUser', { ns: 'components' })}
              </InfoBox>
            )}
          </Content>
        ),
        footer: {
          left: isActiveRoleCompanyAdmin && (
            <Button
              color="secondary"
              loading={sendContactMeRequest.isPending}
              variant="contained"
              onClick={() => sendContactMeRequest.mutate()}
            >
              {t('designLicenseExpiredDialog.contactMeButton', { ns: 'components' })}
            </Button>
          ),
          right: (
            <>
              {isActiveRoleCompanyAdmin && (
                <Button
                  color="primary"
                  disabled={sendContactMeRequest.isPending}
                  variant="contained"
                  onClick={props.onBuyButtonClicked}
                >
                  {t('designLicenseExpiredDialog.buyButton', { ns: 'components' })}
                </Button>
              )}
              <Button
                disabled={sendContactMeRequest.isPending}
                variant="text"
                onClick={props.onClose}
              >
                {isActiveRoleCompanyAdmin
                  ? t('cancel', { ns: 'common' })
                  : t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  strong {
    font-weight: 500;
  }
`;

export { DesignLicenseExpiredDialog };
